import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  IconButton,
  Paper,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Divider,
  AppBar,
  Toolbar,
  useTheme,
  SelectChangeEvent,
  styled,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  LocalPrintshop as ThermalPrinterIcon,
  Add as AddIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
// import { collection, getDocs, query, where } from '../firebase';
// import { db } from '../firebase';
// import { doc, getDoc } from 'firebase-shim';
import indexedDBService from '../services/IndexedDBService';
import InvoiceService from '../services/invoiceService';
import { generateInvoicePDF } from '../utils/invoicePdfGenerator';
import { invoiceTemplateOptions } from '../utils/invoiceTemplates';

// Step components
import ClientSelectionStep from '../components/invoice-steps/ClientSelectionStep';
import ItemsSelectionStep from '../components/invoice-steps/ItemsSelectionStep';
import ChargesDiscountsStep from '../components/invoice-steps/ChargesDiscountsStep';
import InvoicePreviewStep from '../components/invoice-steps/InvoicePreviewStep';
import DashboardLayout from '../components/layouts/DashboardLayout';

// Styled components with modern design
const StepperContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
  borderRadius: 16,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0,0,0,0.3)'
    : '0 2px 12px rgba(0,0,0,0.08)',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#2D2D2D' : '#E0E0E0'}`,
  transition: 'all 0.3s ease'
}));

const StepContentContainer = styled(Box)(({ theme }) => ({
  minHeight: 500,
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
  borderRadius: 16,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0,0,0,0.3)'
    : '0 2px 12px rgba(0,0,0,0.08)',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#2D2D2D' : '#E0E0E0'}`,
  transition: 'all 0.3s ease'
}));

// Interface for step icon props
interface StepIconProps {
  active?: boolean;
  completed?: boolean;
  icon: React.ReactNode;
}

// Modern step icon component
const StepIconComponent = (props: StepIconProps) => {
  const { active, completed, icon } = props;

  const icons: { [index: string]: React.ReactElement } = {
    1: <PersonIcon />,
    2: <ShoppingCartIcon />,
    3: <MoneyIcon />,
    4: <VisibilityIcon />,
  };

  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: completed
          ? 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)'
          : active
            ? 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)'
            : '#E0E0E0',
        color: completed || active ? '#FFFFFF' : '#757575',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: active ? 'scale(1.1)' : 'scale(1)',
        boxShadow: active
          ? '0 4px 12px rgba(25, 118, 210, 0.4)'
          : completed
            ? '0 4px 12px rgba(76, 175, 80, 0.4)'
            : 'none',
      }}
    >
      {completed ? <CheckCircleIcon /> : icons[String(icon)]}
    </Box>
  );
};

// Interface for invoice data
interface InvoiceItem {
  id: number;
  name: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  inventoryItemId?: string;
  hsnCode?: string;
  gstRate?: number;
  taxAmount?: number;
}

// Define an interface for client data with flexible properties
interface ClientData {
  [key: string]: any; // Allow for any property
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  gstin?: string;
  userId?: string;
  createdAt?: string;
}

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';

  // Client information
  client: string;
  clientId: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state: string;
  };

  // Delivery details (new)
  deliveryDetails?: {
    name: string;
    address: string;
    city?: string;
    state?: string;
    pincode?: string;
    phone?: string;
    email?: string;
  };

  // Items
  items: InvoiceItem[];

  // Totals
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;

  // Additional information
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxable: boolean;

  // Additional charges
  additionalCharges?: {
    id: number;
    name: string;
    amount: number;
    taxable?: boolean;
  }[];

  // Metadata
  userId: string;
  createdAt: string;
}

// Steps for invoice creation with descriptions
const steps = [
  'Client Selection',
  'Items & Services',
  'Discounts & Charges',
  'Preview & Finalize'
];

const stepDescriptions = [
  'Select or add a client for this invoice',
  'Add products or services to bill',
  'Apply discounts and additional charges',
  'Review and finalize your invoice'
];

const InvoiceCreation: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // State for stepper
  const [activeStep, setActiveStep] = useState(0);

  // State for loaded data
  const [clients, setClients] = useState<ClientData[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [businessDetails, setBusinessDetails] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Auto-save and draft state
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Invoice data state
  const [invoiceData, setInvoiceData] = useState<InvoiceData>({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'pending',

    // Client information
    client: '',
    clientId: '',
    clientDetails: {
      address: '',
      gstin: '',
      email: '',
      phone: '',
      state: ''
    },

    // Delivery details (initialized from client info)
    deliveryDetails: {
      name: '',
      address: '',
      phone: '',
      email: ''
    },

    // Items
    items: [],

    // Totals
    subtotal: 0,
    tax: 0,
    discount: 0,
    amount: 0,

    // Additional information
    notes: '',
    termsAndConditions: '',
    taxType: 'gst',
    taxRate: 18,
    discountType: 'percentage',
    discountValue: 0,
    taxable: true,

    // Additional charges
    additionalCharges: [],

    // Metadata
    userId: currentUser?.uid || '',
    createdAt: new Date().toISOString(),
  });

  // Load draft on component mount
  useEffect(() => {
    const loadDraftIfExists = async () => {
      if (!currentUser?.uid) return;

      try {
        // Check if there's a draft ID in URL params
        const urlParams = new URLSearchParams(window.location.search);
        const draftIdFromUrl = urlParams.get('draftId');

        if (draftIdFromUrl) {
          const draft = await InvoiceService.loadDraft(draftIdFromUrl, currentUser.uid);
          if (draft) {
            setInvoiceData(draft);
            setDraftId(draftIdFromUrl);
            setLastSaved(new Date(draft.savedAt));
            console.log('Loaded draft from URL:', draftIdFromUrl);
          }
        } else {
          // Load most recent draft if no specific draft ID
          const drafts = await InvoiceService.loadAllDrafts(currentUser.uid);
          if (drafts.length > 0) {
            const mostRecentDraft = drafts[0];
            // Only auto-load if it's less than 24 hours old
            const draftAge = Date.now() - new Date(mostRecentDraft.savedAt).getTime();
            if (draftAge < 24 * 60 * 60 * 1000) {
              setInvoiceData(mostRecentDraft);
              setDraftId(mostRecentDraft.draftId || mostRecentDraft.id);
              setLastSaved(new Date(mostRecentDraft.savedAt));
              console.log('Auto-loaded recent draft:', mostRecentDraft.draftId);
            }
          }
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    };

    loadDraftIfExists();
  }, [currentUser]);

  // Load required data on component mount
  useEffect(() => {
    // Check if we're already initialized
    if (initialized) {
      return;
    }

    // Immediately check for localStorage cache for instant UI display
    try {
      const cachedBusinessDetails = localStorage.getItem('akmo_cache_business');
      if (cachedBusinessDetails) {
        try {
          const parsedBusinessDetails = JSON.parse(cachedBusinessDetails);
          if (parsedBusinessDetails) {
            setBusinessDetails(parsedBusinessDetails);
          }
        } catch (parseError) {
          console.error('Error parsing cached business details:', parseError);
        }
      }

      const cachedClients = localStorage.getItem('akmo_cache_clients');
      if (cachedClients) {
        try {
          const parsedClients = JSON.parse(cachedClients);
          if (parsedClients && Array.isArray(parsedClients) && parsedClients.length > 0) {
            setClients(parsedClients);
          }
        } catch (parseError) {
          console.error('Error parsing cached clients:', parseError);
        }
      }

      const cachedInventory = localStorage.getItem('akmo_cache_inventory');
      if (cachedInventory) {
        try {
          const parsedInventory = JSON.parse(cachedInventory);
          if (parsedInventory && Array.isArray(parsedInventory) && parsedInventory.length > 0) {
            setInventory(parsedInventory);
            setLoading(false);
          }
        } catch (parseError) {
          console.error('Error parsing cached inventory:', parseError);
        }
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }

    // Fetch fresh data from server regardless of localStorage cache
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, initialized]);

  useEffect(() => {
    // Load user settings if available (invoice settings, business profile)
    const loadUserSettings = async () => {
      if (!currentUser) return;

      try {
        const userSettings = await indexedDBService.getItem('userSettings', currentUser.uid) as {
          invoiceSettings?: {
            defaultTemplate?: number
          },
          businessProfile?: {
            logoUrl?: string,
            primaryColor?: string,
            secondaryColor?: string,
          }
        };

        if (userSettings) {
          const data = userSettings;

          setBusinessDetails((prevDetails: any) => {
            const newDetails = { ...prevDetails };
            
            // If invoice settings exist, update the business details with template info
            if (data.invoiceSettings?.defaultTemplate && typeof data.invoiceSettings.defaultTemplate === 'number') {
              const selectedTemplate = invoiceTemplateOptions.find(t => t.id === data.invoiceSettings!.defaultTemplate);
              if (selectedTemplate) {
                newDetails.invoiceTemplate = selectedTemplate;
              }
            }

            // Apply business profile (logo, colors)
            if (data.businessProfile) {
              if (data.businessProfile.logoUrl) newDetails.logoUrl = data.businessProfile.logoUrl;
              if (data.businessProfile.primaryColor) newDetails.primaryColor = data.businessProfile.primaryColor;
              if (data.businessProfile.secondaryColor) newDetails.secondaryColor = data.businessProfile.secondaryColor;
            }

            return newDetails;
          });
        }
      } catch (error) {
        console.error('Error loading user settings:', error);
      }
    };

    loadUserSettings();
  }, [currentUser]);

  const fetchData = async () => {
    try {
      setLoading(true);

      if (!currentUser?.uid) {
        console.error('No user ID available for fetching data');
        setLoading(false);
        return;
      }

      // 1. Get Business Details using new IndexedDB logic
      let businessDetailsData = null;
      try {
        const businesses = await indexedDBService.getAllItems<any>('businesses');
        const userBusiness = businesses.find(b => b.userId === currentUser.uid);
        if (userBusiness) {
          businessDetailsData = userBusiness;
        }
      } catch (error) {
        console.error('Error loading business from IndexedDB:', error);
      }

      // Fallback to cache/service if not found (keeping existing fallback logic logic mostly but simplifying)
      if (!businessDetailsData) {
        // Using cached logic from before as fallback
        try {
          const cachedBusiness = localStorage.getItem('akmo_cache_business');
          if (cachedBusiness) {
            const businessCache = JSON.parse(cachedBusiness);
            const businessFromCache = Array.isArray(businessCache)
              ? businessCache.find(b => b.userId === currentUser.uid)
              : businessCache;

            if (businessFromCache && (businessFromCache.name || businessFromCache.businessName)) {
              businessDetailsData = businessFromCache;
            }
          }
        } catch (cacheError) { console.error(cacheError); }
      }

      if (businessDetailsData) {
        setBusinessDetails(businessDetailsData);
      }

      // Generate Invoice Number
      // Use the business name to generate a custom invoice number if available
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const randomPart = Math.floor(Math.random() * 1000).toString().padStart(3, '0');

      const businessName = businessDetails?.name || businessDetails?.businessName || '';
      let invoiceNumber = '';

      if (businessName) {
        try {
          invoiceNumber = await InvoiceService.generateInvoiceNumber(currentUser.uid, businessName);
        } catch (e) {
          const prefix = businessName.substring(0, 3).toUpperCase();
          invoiceNumber = `${prefix}-${year}${month}${day}-${randomPart}`;
        }
      } else {
        invoiceNumber = `INV-${year}${month}${day}-${randomPart}`;
      }

      setInvoiceData(prev => ({
        ...prev,
        invoiceNumber
      }));

      // 2. Fetch Clients using IndexedDB
      try {
        const clientsData = await indexedDBService.getAllItems<any>('clients');
        const userClients = clientsData.filter(c => c.userId === currentUser.uid);
        if (userClients.length > 0) {
          // Map to ClientData interface if needed
          const mappedClients: ClientData[] = userClients.map(c => ({
            ...c,
            userId: c.userId || currentUser.uid
          }));
          setClients(mappedClients);
          localStorage.setItem('akmo_cache_clients', JSON.stringify(mappedClients));
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      }

      // 3. Fetch Inventory using IndexedDB
      try {
        const inventoryData = await indexedDBService.getAllItems<any>('inventory');
        const userInventory = inventoryData.filter(i => i.userId === currentUser.uid);

        if (userInventory.length > 0) {
          const processedInventory = userInventory.map(item => ({
            id: item.id,
            name: item.name || item.itemName || 'Unnamed',
            description: item.description || '',
            price: item.price || item.rate || item.sellingPrice || 0,
            hsnCode: item.hsnCode || '',
            gstRate: item.gstRate || 18,
            category: item.category || 'Uncategorized',
            unit: item.unit || 'Nos',
            stock: item.stock || item.stockQuantity || 0
          }));
          setInventory(processedInventory);
          localStorage.setItem('akmo_cache_inventory', JSON.stringify(processedInventory));
        } else {
          // Mock data only if no real data found
          createMockInventory();
        }
      } catch (error) {
        console.error('Error fetching inventory:', error);
      }

      setInitialized(true);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to create mock inventory items for development
  const createMockInventory = () => {
    if (process.env.NODE_ENV === 'development') {
      const mockInventory = [
        {
          id: 'mock-item-1',
          name: 'Sample Product 1',
          description: 'This is a sample product for testing',
          price: 1000,
          hsnCode: '1234',
          gstRate: 18,
          category: 'Sample',
          unit: 'Nos',
          stock: 10,
          userId: currentUser?.uid || '',
          createdAt: new Date().toISOString()
        },
        {
          id: 'mock-item-2',
          name: 'Sample Service 1',
          description: 'This is a sample service for testing',
          price: 2000,
          hsnCode: '9954',
          gstRate: 18,
          category: 'Services',
          unit: 'Hrs',
          stock: 100,
          userId: currentUser?.uid || '',
          createdAt: new Date().toISOString()
        },
      ];

      setInventory(mockInventory);

      // Save mock data to localStorage
      try {
        localStorage.setItem('cachedInventory', JSON.stringify(mockInventory));
      } catch (err) {
        console.error('Error saving mock inventory to localStorage:', err);
      }
    }
  };

  // Handle step navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle invoice data updates
  const updateInvoiceData = (data: Partial<InvoiceData>) => {
    setInvoiceData(prev => ({
      ...prev,
      ...data
    }));
    setHasUnsavedChanges(true);
  };

  // Auto-save effect
  useEffect(() => {
    // Don't auto-save if no changes or no user
    if (!hasUnsavedChanges || !currentUser?.uid) return;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for 30 seconds
    autoSaveTimerRef.current = setTimeout(async () => {
      await handleAutoSave();
    }, 30000); // 30 seconds

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [invoiceData, hasUnsavedChanges, currentUser]);

  // Handle auto-save
  const handleAutoSave = async () => {
    if (!currentUser?.uid) return;

    try {
      setIsSaving(true);
      const draft = await InvoiceService.saveDraft({
        ...invoiceData,
        draftId: draftId || undefined
      }, currentUser.uid);

      setDraftId(draft.draftId);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      console.log('Auto-saved draft:', draft.draftId);
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle manual save as draft
  const handleSaveDraft = async () => {
    if (!currentUser?.uid) return;

    try {
      setIsSaving(true);
      const draft = await InvoiceService.saveDraft({
        ...invoiceData,
        draftId: draftId || undefined
      }, currentUser.uid);

      setDraftId(draft.draftId);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      alert('Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle payment status change
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    updateInvoiceData({ status: event.target.value as 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted' });
  };

  // Handle invoice date change
  const handleInvoiceDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateInvoiceData({ invoiceDate: event.target.value });
  };

  // Handle due date change
  const handleDueDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateInvoiceData({ dueDate: event.target.value });
  };

  // Handle invoice number change
  const handleInvoiceNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateInvoiceData({ invoiceNumber: event.target.value });
  };

  // Handle save invoice
  const handleSaveInvoice = async (status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted' | 'email' | 'print' | 'export' = 'pending') => {
    try {
      // Always use AkMo Invoice template (ID: 1)
      const templateId = 1;

      // Format the invoice data for saving
      const invoiceToSave = {
        ...invoiceData,
        status: status === 'email' || status === 'print' || status === 'export' ? 'pending' : status,
        template: templateId, // Always use AkMo Invoice template (ID: 1)
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Add missing required properties
        date: invoiceData.invoiceDate, // Use invoiceDate as date
        bankDetails: businessDetails?.bankDetails || {} // Use business bank details or empty object
      };

      // Save the invoice using the InvoiceService
      const savedInvoice = await InvoiceService.createInvoice(invoiceToSave, currentUser?.uid || '');

      // Add success feedback
      alert(`Invoice saved successfully with status: ${status}`);

      // Navigate back to invoices page only if it's a "final" save, not when called from other functions
      if (!['export', 'print', 'email'].includes(status)) {
        navigate('/invoices');
      }

      return savedInvoice;
    } catch (error) {
      console.error('Error saving invoice:', error);
      // Show error message to user
      alert('Error saving invoice. Please try again.');
      throw error;
    }
  };

  // Format business details helper function for consistent use
  const formatBusinessDetailsForTemplate = () => {
    return {
      name: businessDetails?.name || '',
      address: businessDetails?.address || '',
      city: businessDetails?.city || '',
      state: businessDetails?.state || '',
      pincode: businessDetails?.pincode || '',
      phone: businessDetails?.phone || '',
      email: businessDetails?.email || '',
      gstin: businessDetails?.gstin || '',
      bankName: businessDetails?.bankDetails?.bankName || '',
      bankBranch: businessDetails?.bankDetails?.branch || '',
      accountNumber: businessDetails?.bankDetails?.accountNumber || '',
      ifsc: businessDetails?.bankDetails?.ifsc || '',
      upiId: businessDetails?.bankDetails?.upiId || '',
      logoUrl: businessDetails?.logoUrl || '',
    };
  };

  // Format client details helper function for consistent use
  const formatClientDetailsForTemplate = () => {
    return {
      name: invoiceData.client,
      address: invoiceData.clientDetails.address,
      city: '',
      state: invoiceData.clientDetails.state || '',
      pincode: '',
      phone: invoiceData.clientDetails.phone,
      email: invoiceData.clientDetails.email,
      gstin: invoiceData.clientDetails.gstin,
    };
  };

  // Handle email invoice
  const handleEmailInvoice = async () => {
    try {
      // First save the invoice with 'pending' status but mark as 'email' to avoid navigation
      const savedInvoice = await handleSaveInvoice('email');
      if (!savedInvoice) {
        console.error('Failed to save invoice before email');
        return;
      }

      // Always use AkMo Invoice template (ID: 1)
      const templateId = 1;

      // Generate PDF for the invoice using AkMoInvoiceFormat
      const pdfBytes = await generateInvoicePDF(
        {
          ...invoiceData,
          businessDetails: formatBusinessDetailsForTemplate(),
          status: 'pending'
        },
        businessDetails,
        templateId // Always pass template ID 1
      );

      // Use the EmailService to send the email with PDF attachment
      const emailTo = invoiceData.clientDetails.email || '';
      const subject = `Invoice ${invoiceData.invoiceNumber} from ${businessDetails?.name || 'Business'}`;
      const body = `Dear ${invoiceData.client},\n\nPlease find attached the invoice ${invoiceData.invoiceNumber} for your recent purchase.\n\nTotal Amount: ${new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(invoiceData.amount)}\nDue Date: ${invoiceData.dueDate}\n\nThank you for your business.\n\nRegards,\n${businessDetails?.name || ''}`;

      // Use imported EmailService to send the email
      const { default: EmailService } = await import('../utils/emailService');
      await EmailService.sendInvoiceEmail(
        emailTo,
        subject,
        body.replace(/\n/g, '<br/>'), // Convert newlines to HTML breaks
        pdfBytes,
        `Invoice_${invoiceData.invoiceNumber}.pdf`
      );

      alert('Email sent successfully');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email. Please try again.');
    }
  };

  // Handle print invoice
  const handlePrintInvoice = async () => {
    try {
      // First save the invoice with 'pending' status but mark as 'print' to avoid navigation
      const savedInvoice = await handleSaveInvoice('print');
      if (!savedInvoice) {
        console.error('Failed to save invoice before printing');
        return;
      }

      // Format invoice details
      const invoiceDetails = {
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: invoiceData.invoiceDate,
        dueDate: invoiceData.dueDate,
        discount: invoiceData.discount,
        termsAndConditions: invoiceData.termsAndConditions ?
          invoiceData.termsAndConditions.split('\n') : [],
      };

      // Format items
      const items = invoiceData.items.map(item => ({
        productName: item.name,
        description: item.description || '',
        quantity: item.quantity,
        unit: 'Nos',
        hsn: item.hsnCode || '',
        gstRate: item.gstRate || 0,
        taxableValue: item.amount / (1 + (item.gstRate || 0) / 100),
        taxAmount: item.taxAmount || 0,
        total: item.amount,
      }));

      // Always use AkMo Invoice template (ID: 1)
      const templateId = 1;

      // Use InvoicePrinter utility to print with AkMoInvoiceFormat
      const { default: InvoicePrinter } = await import('../utils/invoicePrinter');
      await InvoicePrinter.printInvoice(
        formatBusinessDetailsForTemplate(),
        formatClientDetailsForTemplate(),
        invoiceDetails,
        items,
        templateId // Always use AkMo Invoice template (ID: 1)
      );

    } catch (error) {
      console.error('Error printing invoice:', error);
      alert('Error printing invoice. Please try again.');
    }
  };

  // Handle save PDF
  const handleSavePdf = async () => {
    try {
      // First save the invoice with 'pending' status but mark as 'export' to avoid navigation
      const savedInvoice = await handleSaveInvoice('export');

      if (!savedInvoice) {
        console.error('Failed to save invoice before PDF generation');
        return;
      }

      // Always use AkMo Invoice template (ID: 1)
      const templateId = 1;

      // Generate PDF for the invoice using AkMoInvoiceFormat
      const pdfBytes = await generateInvoicePDF(
        {
          ...invoiceData,
          businessDetails: formatBusinessDetailsForTemplate(),
          status: 'pending' // Ensure status is set to pending
        },
        businessDetails, // Full business details for the PDF generator
        templateId // Always pass template ID 1
      );

      // Convert to Blob and download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceData.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error saving PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Handle thermal print
  const handleThermalPrint = async () => {
    try {
      // Format thermal invoice details
      const thermalInvoiceDetails = {
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: invoiceData.invoiceDate,
        items: invoiceData.items.map(item => ({
          description: item.name,
          quantity: item.quantity,
          rate: item.rate,
          gstRate: item.gstRate || 0,
          amount: item.amount,
        })),
        discount: invoiceData.discount,
        isInterstate: businessDetails?.state !== invoiceData.clientDetails.state,
        placeOfSupply: invoiceData.clientDetails.state,
        // Add additional data for our new thermal formats
        termsAndConditions: invoiceData.termsAndConditions ?
          invoiceData.termsAndConditions.split('\n') : [],
        additionalCharges: invoiceData.additionalCharges || []
      };

      // Format business details with GST registration information
      const businessDetailsForPrint = {
        ...formatBusinessDetailsForTemplate(),
        // Include GST registration information
        gstRegistered: businessDetails?.gstRegistered || 'not_registered',
        gstType: businessDetails?.gstType || 'regular'
      };

      // Use InvoicePrinter utility to print thermal receipt
      const { default: InvoicePrinter } = await import('../utils/invoicePrinter');
      await InvoicePrinter.printThermalInvoice(
        businessDetailsForPrint,
        formatClientDetailsForTemplate(),
        thermalInvoiceDetails
      );

    } catch (error) {
      console.error('Error printing thermal receipt:', error);
    }
  };

  // Handle create next invoice
  const handleCreateNextInvoice = async () => {
    // Save current invoice first
    await handleSaveInvoice('pending');

    // Reset form for next invoice
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const randomPart = Math.floor(Math.random() * 1000).toString().padStart(3, '0');

    setInvoiceData({
      ...invoiceData,
      invoiceNumber: `INV-${year}${month}${day}-${randomPart}`,
      items: [],
      subtotal: 0,
      tax: 0,
      discount: 0,
      amount: 0,
      discountValue: 0,
      additionalCharges: [],
      status: 'pending',
      createdAt: new Date().toISOString()
    });

    // Reset to first step
    setActiveStep(0);
  };

  // Render step content
  const renderStepContent = (step: number) => {
    // Show loading indicator when we're still fetching clients
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading client data...
          </Typography>
        </Box>
      );
    }

    switch (step) {
      case 0:
        return (
          <ClientSelectionStep
            invoiceData={invoiceData}
            updateInvoiceData={updateInvoiceData}
            clients={clients}
            userId={currentUser?.uid || ''}
            onClientAdded={(newClient) => {
              // Add the new client to the clients list
              setClients(prev => {
                // Check if client already exists
                if (prev.some(c => c.id === newClient.id)) {
                  return prev.map(c => c.id === newClient.id ? newClient : c);
                }
                return [...prev, newClient];
              });
            }}
          />
        );
      case 1:
        return (
          <ItemsSelectionStep
            invoiceData={invoiceData}
            updateInvoiceData={updateInvoiceData}
            inventory={inventory}
            userId={currentUser?.uid || ''}
          />
        );
      case 2:
        return (
          <ChargesDiscountsStep
            invoiceData={invoiceData}
            updateInvoiceData={updateInvoiceData}
          />
        );
      case 3:
        return (
          <InvoicePreviewStep
            invoiceData={invoiceData}
            businessDetails={businessDetails}
            onPrint={handlePrintInvoice}
            onThermalPrint={handleThermalPrint}
            onEmail={handleEmailInvoice}
            onSavePdf={handleSavePdf}
            onCreateNextInvoice={handleCreateNextInvoice}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => navigate('/invoices')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <ReceiptIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Create New Invoice
          </Typography>

          {/* Auto-save indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isSaving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Saving...
                </Typography>
              </Box>
            )}
            {!isSaving && lastSaved && (
              <Typography variant="caption" color="text.secondary">
                Last saved: {lastSaved.toLocaleTimeString()}
              </Typography>
            )}
            {!isSaving && !lastSaved && hasUnsavedChanges && (
              <Typography variant="caption" color="warning.main">
                Unsaved changes
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Top Navigation Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        p: 2,
        backgroundColor: 'transparent',
        gap: 2,
        width: '100%',
        maxWidth: '800px',
        mx: 'auto'
      }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: 3,
            minWidth: '120px',
            borderWidth: 2,
            fontWeight: 600,
            textTransform: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderWidth: 2,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }
          }}
        >
          Back
        </Button>

        <Button
          variant="outlined"
          onClick={handleSaveDraft}
          startIcon={<SaveIcon />}
          disabled={isSaving}
          sx={{
            borderRadius: 3,
            minWidth: '140px',
            borderWidth: 2,
            fontWeight: 600,
            textTransform: 'none',
            borderColor: 'success.main',
            color: 'success.main',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderWidth: 2,
              borderColor: 'success.dark',
              backgroundColor: 'success.light',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
            }
          }}
        >
          Save as Draft
        </Button>

        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => handleSaveInvoice('pending')}
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
              color: 'white',
              minWidth: '160px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.5)'
              }
            }}
          >
            Finalize & Save
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
              color: 'white',
              minWidth: '120px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.5)'
              }
            }}
          >
            Next
          </Button>
        )}
      </Box>

      {/* Invoice Details Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 4,
          background: theme => theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1E1E1E 0%, #2D2D2D 100%)'
            : 'linear-gradient(135deg, #FFFFFF 0%, #F5F7FA 100%)',
          border: theme => `1px solid ${theme.palette.mode === 'dark' ? '#2D2D2D' : '#E0E0E0'}`,
          boxShadow: theme => theme.palette.mode === 'dark'
            ? '0 4px 20px rgba(0,0,0,0.3)'
            : '0 2px 12px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ReceiptIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
          <Typography variant="h6" fontWeight={600}>
            Invoice Details
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Invoice Number"
              variant="outlined"
              size="small"
              value={invoiceData.invoiceNumber}
              onChange={handleInvoiceNumberChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Invoice Date"
              type="date"
              variant="outlined"
              size="small"
              value={invoiceData.invoiceDate}
              onChange={handleInvoiceDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              variant="outlined"
              size="small"
              value={invoiceData.dueDate}
              onChange={handleDueDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="payment-status-label">Payment Status</InputLabel>
              <Select
                labelId="payment-status-label"
                value={invoiceData.status}
                label="Payment Status"
                onChange={handleStatusChange}
                sx={{
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  }
                }}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Payment Pending</MenuItem>
                <MenuItem value="paid">Payment Received</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Stepper */}
      <StepperContainer>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            '& .MuiStepLabel-label': {
              marginTop: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              '&.Mui-active': {
                fontWeight: 600,
                color: 'primary.main'
              },
              '&.Mui-completed': {
                fontWeight: 600,
                color: 'success.main'
              }
            },
            '& .MuiStepConnector-line': {
              borderTopWidth: 2,
              borderRadius: 1
            }
          }}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={StepIconComponent}
                optional={
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {stepDescriptions[index]}
                  </Typography>
                }
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </StepperContainer>

      {/* Step Content */}
      <StepContentContainer sx={{ flex: 1, overflowY: 'auto' }}>
        {renderStepContent(activeStep)}
      </StepContentContainer>

      {/* Floating Navigation Buttons */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: { xs: 16, sm: 40, md: 72 }, // Start from sidebar with responsive offsets
          right: 16,
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          width: 'auto',
          pointerEvents: 'none' // This makes the container pass through clicks
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: theme => theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 4,
            gap: 2,
            width: 'auto',
            maxWidth: '800px',
            boxShadow: theme => theme.palette.mode === 'dark'
              ? '0 8px 16px rgba(0, 0, 0, 0.5)'
              : '0 8px 16px rgba(0, 0, 0, 0.1)',
            pointerEvents: 'auto' // Re-enable pointer events for the Paper
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: 2,
              minWidth: '120px',
              color: theme => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main'
            }}
          >
            Back
          </Button>

          <Button
            variant="outlined"
            onClick={() => handleSaveInvoice('draft')}
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: 2,
              minWidth: '120px',
              color: theme => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main'
            }}
          >
            Save as Draft
          </Button>

          {activeStep === steps.length - 1 && (
            <>
              <Button
                variant="outlined"
                onClick={handlePrintInvoice}
                startIcon={<PrintIcon />}
                sx={{
                  borderRadius: 2,
                  minWidth: '120px',
                  color: theme => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main'
                }}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                onClick={handleSavePdf}
                startIcon={<DownloadIcon />}
                sx={{
                  borderRadius: 2,
                  minWidth: '120px',
                  color: theme => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main'
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="outlined"
                onClick={handleEmailInvoice}
                startIcon={<EmailIcon />}
                sx={{
                  borderRadius: 2,
                  minWidth: '120px',
                  color: theme => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main'
                }}
              >
                Email
              </Button>
            </>
          )}

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => handleSaveInvoice('pending')}
              startIcon={<SaveIcon />}
              sx={{
                borderRadius: 2,
                backgroundImage: theme => theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #1565C0 30%, #0288D1 90%)'
                  : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                color: 'white',
                minWidth: '120px'
              }}
            >
              Finalize & Save
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderRadius: 2,
                backgroundImage: theme => theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #1565C0 30%, #0288D1 90%)'
                  : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                color: 'white',
                minWidth: '120px'
              }}
            >
              Next
            </Button>
          )}
        </Paper>
      </Box>

      {/* Add padding at the bottom to prevent content from being hidden behind the floating buttons */}
      <Box sx={{ height: '70px' }} />
    </Box>
  );
};

export default InvoiceCreation; 
